/*
  © 2025 Jhango
  https://www.jhango.com
*/

// Init Bootstrap tooltips/popovers.
// Event-driven instead of a 1s setInterval that ran forever on every page:
// tooltips live in dynamically re-rendered markup (cart drawer, swatches), so
// we re-init on load, on section load, and on debounced DOM mutations.
(function () {
  function initBsToggles() {
    if (!window.bootstrap) return;
    document
      .querySelectorAll('[data-bs-toggle="tooltip"]')
      .forEach((el) => window.bootstrap.Tooltip.getOrCreateInstance(el));
    document
      .querySelectorAll('[data-bs-toggle="popover"]')
      .forEach((el) => window.bootstrap.Popover.getOrCreateInstance(el));
  }

  let pending;
  function scheduleInit() {
    clearTimeout(pending);
    pending = setTimeout(initBsToggles, 150);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleInit);
  } else {
    scheduleInit();
  }
  document.addEventListener("shopify:section:load", scheduleInit);
  new MutationObserver(scheduleInit).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();

// Debouce
window.theme.debounce = function (callback, wait = 200) {
  let timeout;
  return (...args) => {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => callback.apply(context, args), wait);
  };
};

// Throttle
window.theme.throttle = function (callback, timeFrame = 200) {
  let lastTime = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastTime >= timeFrame) {
      callback(...args);
      lastTime = now;
    }
  };
};

// Create cookie
window.theme.createCookie = function (name, value, days) {
  let date, expires;
  if (days) {
    date = new Date();
    date.setDate(date.getDate() + days);
    expires = "; expires=" + date.toUTCString();
  } else {
    expires = "";
  }
  document.cookie = name + "=" + value + expires + "; path=/";
};

// Detect scroll
window.addEventListener("scroll", () => {
  if (window.scrollY > 0) {
    document.documentElement.classList.add("has-scrolled");
  } else {
    document.documentElement.classList.remove("has-scrolled");
  }
});

// Calculate "xx time ago"
window.theme.calcTimeAgo = function (timestamp) {
  const now = new Date().getTime();
  const diff = now - timestamp;

  let text;

  if (diff < 60000) {
    text = window.theme.locales.times.moments;
  } else if (diff < 3.6e6) {
    const min = Math.round(diff / 60000);
    text =
      min === 1
        ? `${min} ${window.theme.locales.times.minute}`
        : `${min} ${window.theme.locales.times.minutes}`;
  } else if (diff < 8.64e7) {
    const hours = Math.round(diff / 3.6e6);
    text =
      hours === 1
        ? `${hours} ${window.theme.locales.times.hour}`
        : `${hours} ${window.theme.locales.times.hours}`;
  } else {
    const days = Math.round(diff / 8.64e7);
    text =
      days === 1
        ? `${days} ${window.theme.locales.times.day}`
        : `${days} ${window.theme.locales.times.days}`;
  }

  return `${text} ${window.theme.locales.times.ago}`;
};

// Format money
window.Shopify.formatMoney = function (
  cents,
  moneyFormat = window.Shopify.moneyFormat
) {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }

  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function defaultOption(opt, def) {
    return typeof opt === "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split(".");
    const dollars = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1" + thousands
    );
    const cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (moneyFormat.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  return moneyFormat.replace(placeholderRegex, value);
};

// Resize images
window.Shopify.resizeImage = function (src, size, crop = "") {
  return src
    .replace(
      /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g,
      "."
    )
    .replace(/\.jpg|\.png|\.gif|\.jpeg/g, (match) => {
      if (crop.length) {
        crop = `_crop_${crop}`;
      }
      return `_${size}${crop}${match}`;
    });
};

// Shopify's callenge page
document
  .querySelector(".btn.shopify-challenge__button")
  ?.classList.add("btn-primary");

// Shopify's errors messages
document.querySelectorAll(".errors").forEach((elem) => {
  elem.classList.add("alert", "alert-danger");
});

// Detect viewport (mostly for animations)
const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("viewport-entered");
      } else {
        entry.target.classList.remove("viewport-entered");
      }
    });
  },
  { threshold: 0.4 }
);
document.querySelectorAll(".viewport-detect").forEach((el) => {
  observer.observe(el);
});

// Lazy load background images
const lazyBgObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add("lazy-bg-visible");
      lazyBgObserver.unobserve(entry.target);
    }
  });
});
document.querySelectorAll(".lazy-bg").forEach((el) => {
  lazyBgObserver.observe(el);
});

// Lazy load autoplayed videos
const lazyVideosObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.play();
      }
    });
  },
  { rootMargin: "0px 0px 300px 0px" }
);
document.querySelectorAll('video[data-autoplay="true"]').forEach((el) => {
  lazyVideosObserver.observe(el);
});

class SwiperSlider extends HTMLElement {
  constructor() {
    super();

    this.init();

    document.addEventListener("shopify:section:load", (event) => {
      if (event.detail.sectionId === this.dataset.sectionId) {
        this.init();
      }
    });
  }

  init() {
    this.slider = new window.Swiper(this, {
      speed: this.speed,
      autoplay: this.autoplay,
      navigation: this.navigation,
      pagination: this.pagination,
      scrollbar: this.scrollbar,
      breakpoints: this.breakpoints,
      rewind: true,
      watchSlidesProgress: true,
      mousewheel: {
        enabled: true,
        forceToAxis: true,
      },
    });
  }

  speed = Number(this.dataset.sliderSpeed);

  autoplay =
    this.dataset.sliderAutoplay === "0"
      ? undefined
      : { delay: Number(this.dataset.sliderAutoplay) * 1000 };

  navigation = {
    enabled: this.dataset.sliderNavigation === "true",
    prevEl: ".swiper-button-prev",
    nextEl: ".swiper-button-next",
  };

  pagination = {
    enabled: this.dataset.sliderPagination === "true",
    el: ".swiper-pagination",
    type: this.dataset.sliderPaginationType,
    dynamicBullets: true,
    dynamicMainBullets: 2,
    renderFraction: function (currentClass, totalClass) {
      return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`;
    },
  };

  scrollbar = {
    enabled: this.dataset.sliderScrollbar === "true",
    el: this.querySelector(".swiper-scrollbar"),
    draggable: true,
  };

  partialSlideMobile = this.dataset.sliderPartialSlidesMobile === "true";

  breakpoints = {
    0: {
      slidesPerView:
        Number(this.dataset.breakpointXs || 1) *
        (this.partialSlideMobile ? 1.2 : 1),
      spaceBetween: 16,
    },
    500: {
      slidesPerView: Number(this.dataset.breakpointSm || 1),
      spaceBetween: 16,
    },
    800: {
      slidesPerView: Number(this.dataset.breakpointMd || 1),
      spaceBetween: 16,
    },
    1200: {
      slidesPerView: Number(this.dataset.breakpointLg || 1),
      spaceBetween: 16,
    },
    1600: {
      slidesPerView: Number(this.dataset.breakpointXl || 1),
      spaceBetween: 20,
    },
  };
}
customElements.define("swiper-slider", SwiperSlider);

class AnimatedCountdown extends HTMLElement {
  constructor() {
    super();

    if (!this.dataset.time.length) {
      this.innerHTML = "Please add the unix time in the settings";
    }

    let countDownDate = Number(this.dataset.time) * 1000;

    if (window.location.href.includes("ks-")) {
      countDownDate = Date.now() + 4.32e7;
    }

    const textTimes = window.theme.locales.times;

    let d = textTimes.d;
    let h = textTimes.h;
    let m = textTimes.m;
    let s = textTimes.s;

    if (this.dataset.format === "long") {
      d = textTimes.days;
      h = textTimes.hours;
      m = textTimes.minutes;
      s = textTimes.seconds;
    }

    const x = setInterval(() => {
      const now = new Date().getTime();
      const distance = countDownDate - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const min = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const sec = Math.floor((distance % (1000 * 60)) / 1000);

      if (days > 0) {
        this.querySelector("[data-days]").innerHTML = `${days}<em>${d}</em>`;
      } else {
        this.querySelector("[data-days]")?.parentElement.remove();
      }
      if (hours > 0) {
        this.querySelector("[data-hours]").innerHTML = `${hours}<em>${h}</em>`;
      } else {
        this.querySelector("[data-hours]")?.parentElement.remove();
      }
      this.querySelector("[data-min]").innerHTML = `${min}<em>${m}</em>`;
      this.querySelector("[data-sec]").innerHTML = `${sec}<em>${s}</em>`;

      if (distance < 0) {
        clearInterval(x);
        this.innerHTML = textTimes.expired;
      }
    }, 1000);
  }
}
customElements.define("animated-countdown", AnimatedCountdown);

class ShareButton extends HTMLElement {
  constructor() {
    super();

    this.btn = this.querySelector("button");

    this.btn.addEventListener("click", () => {
      this.onBtnClick();
    });
  }

  onBtnClick() {
    if (navigator.share) {
      navigator.share({
        title: this.btn.dataset.shareTitle,
        url: window.location.href,
      });
    } else {
      const popover = window.bootstrap.Popover.getOrCreateInstance(this.btn, {
        content: `
          <div class="input-group input-group-sm">
            <input type="text" class="form-control text-truncate" value="${window.location.href}">
            <button id="share-btn-popover-btn" class="btn btn-outline-secondary" type="button">${this.btn.dataset.textCopy}</button>
          </div>
        `,
        html: true,
        sanitize: false,
        placement: "top",
      });

      popover.show();

      document
        .querySelector("#share-btn-popover-btn")
        ?.addEventListener("click", (e) => {
          navigator.clipboard.writeText(window.location.href);
          e.target.textContent = this.btn.dataset.textCopied;
        });

      setTimeout(() => {
        popover.hide();
        this.btn.blur();
      }, 5000);
    }
  }
}
customElements.define("share-button", ShareButton);

/*
  Localization form
*/
document
  .querySelectorAll(".shopify-localization-form button")
  .forEach((btn) => {
    btn.addEventListener("click", () => {
      btn.closest("form").querySelector('[name="country_code"]').value =
        btn.dataset.isoCode;
      btn.closest("form").submit();
    });
  });

document.addEventListener('DOMContentLoaded', function() {
  return true;

});
  function validateLicense(url) {
    return true;
  }